import { intlGet } from '@/locales';
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'umi';
import { DefaultPage } from '../tdesign';
// config
import { API } from '@/config';
// hooks
import { NetworkTypeEnum, useNetworkStatus } from './useNetworkStatus';
import { useRecaptchaResourceMonitor } from './useRecaptchaResourceMonitor';
// utils
import { GoogleCaptchaVersionEnum } from '@/pages/adminManage/utils/typing/login';
import { addBy } from '../../utils/format/number';
// type
import type { LanguageType } from '@/definition/common/data.d';
import { StatusTipEnum } from '@/definition/common/data.d';
import type { ConnectState } from '@/models/connect.d';
import { DefaultPageStatusEnum } from '../tdesign/data.d';

// 验证码错误类型
export enum GoogleCaptureErrorEnum {
  /**
   * 一级资源加载失败
   * 人工复现操作：控制台: 选择资源 => 右键点击 => Block request domain 阻止请求域
   */
  FIRST_LEVEL_LOAD_FAILED = 'firstLevelLoadFailed',
  /** 一级资源超时 */
  FIRST_LEVEL_TIMEOUT = 'firstLevelTimeout',
  /** 次级资源加载失败 */
  SECOND_LEVEL_LOAD_FAILED = 'secondLevelLoadFailed',
  /**
   * grecaptcha 对象未成功初始化
   * 人工复现操作：控制台: 选择资源 => 右键点击 => Override content 重写内容
   */
  SCRIPT_ERROR = 'scriptError',
  /**
   * 验证执行失败
   * 人工复现操作：V3: 未知; V2: 更改sitekey
   */
  EXECUTE_ERROR = 'executeError',
  /** 验证执行超时 */
  EXECUTE_TIMEOUT = 'executeTimeout',
  /** 组件已被卸载 */
  COMPONENT_UNMOUNTED = 'componentUnmountmounted',
  /** 网络错误 */
  NETWORK_ERROR = 'networkError',
  /** 未知错误 (补充兜底) */
  UNKNOWN_ERROR = 'unknownError',
}
export const googleCaptureErrorOpts = {
  [GoogleCaptureErrorEnum.FIRST_LEVEL_LOAD_FAILED]: '一级资源加载失败',
  [GoogleCaptureErrorEnum.FIRST_LEVEL_TIMEOUT]: '一级资源超时',
  [GoogleCaptureErrorEnum.SECOND_LEVEL_LOAD_FAILED]: '次级资源加载失败',
  [GoogleCaptureErrorEnum.SCRIPT_ERROR]: 'grecaptcha 对象未成功初始化',
  [GoogleCaptureErrorEnum.EXECUTE_ERROR]: '验证执行失败',
  [GoogleCaptureErrorEnum.EXECUTE_TIMEOUT]: '验证执行超时',
  [GoogleCaptureErrorEnum.COMPONENT_UNMOUNTED]: '组件已被卸载',
  [GoogleCaptureErrorEnum.NETWORK_ERROR]: '网络错误',
  [GoogleCaptureErrorEnum.UNKNOWN_ERROR]: '未知错误',
};

type PropsType = {
  localLanguage: LanguageType;
  version?: GoogleCaptchaVersionEnum;
  onChange: (token: string) => void;
  /** 加载失败 */
  onFail?: (errorType?: GoogleCaptureErrorEnum) => void;
};

function GooglCapture(props: PropsType) {
  const {
    localLanguage,
    version = GoogleCaptchaVersionEnum.V2,
    onChange,
    onFail,
  } = props;
  // google 验证码js
  const googleApi = 'https://www.google.com/recaptcha/api.js';

  const captchaRef = useRef<HTMLDivElement>(null);
  // 保存 v2 的 widgetId
  // const captchaWidgetRef = useRef<any>(null);
  /**
   * 防重入锁
   * 无论是因为“一级超时”、“v3执行超时”、“雷达监听到次级报错”还是“V2的 error-callback”，最终都会汇聚到 handleFail() 函数。
   * 通过 isFailedRef.current 判断，保证 onFail() 在一次生命周期内绝对只被调用一次，避免向业务层发起多次降级请求。
   */
  const isFailedRef = useRef(false);
  /**
   * 卸载保护锁
   * 网络极差时，用户可能等不及，直接把组件（比如弹窗）关了。如果不加保护，x秒后 Promise.race 抛出超时错误，试图 setReqStatus 和调用 onFail，React 会报内存泄漏警告。
   * 加上 isMountedRef 后，组件卸载后的所有异步回调都会被直接 return 忽略。
   */
  const isMountedRef = useRef(true);
  // 管理 V2 兜底定时器
  const v2RenderTimerRef = useRef<NodeJS.Timeout | null>(null);
  // 管理 V2 验证勾选人机验证后执行超时定时器
  const v2ExecuteTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [loadFlag, setLoadFlag] = useState(true);

  const [reqStatus, setReqStatus] = useState(StatusTipEnum.init);

  // 获取当前网络状态
  const { networkType } = useNetworkStatus();

  // 根据网络状况动态设置超时
  const CAPTCHA_CONFIG = useMemo(() => {
    // v2方式验证等待用户勾选&图形操作时间 10min
    const waitingExcute = 10 * 60 * 1000;
    // 默认超时配置
    const config = {
      [GoogleCaptchaVersionEnum.V3]: {
        // 一级资源超时 2s
        FIRST_LEVEL_TIMEOUT: 2000,
        // execute 获取 token阶段 8s
        EXECUTE_TIMEOUT: 8000,
      },
      [GoogleCaptchaVersionEnum.V2]: {
        // 一级资源超时 0.5s
        FIRST_LEVEL_TIMEOUT: 500,
        // 调用 render 后，如果 x 时长内用户还没点出对勾（没触发 callback 获取到token），说明次级资源可能挂了
        RENDER_TIMEOUT: 600,
        // 等待 Google 内部的加载球消失，再切换状态为 success
        SUCCESS_TIMEOUT: 1000,
        // 勾选人机验证后限制验证时长 5min
        EXECUTE_TIMEOUT: 5 * 60 * 1000,
      },
    };

    // 根据网络类型调整超时
    if (
      [NetworkTypeEnum.ThreeG, NetworkTypeEnum.Unknown].includes(networkType)
    ) {
      config[GoogleCaptchaVersionEnum.V3].FIRST_LEVEL_TIMEOUT = 3000;
      config[GoogleCaptchaVersionEnum.V3].EXECUTE_TIMEOUT = 20000;
      config[GoogleCaptchaVersionEnum.V2].FIRST_LEVEL_TIMEOUT = 1200;
      config[GoogleCaptchaVersionEnum.V2].RENDER_TIMEOUT = 6000;
      config[GoogleCaptchaVersionEnum.V2].SUCCESS_TIMEOUT = 4000;
      config[GoogleCaptchaVersionEnum.V2].EXECUTE_TIMEOUT = 5 * 60 * 1000;
    }
    if (
      [NetworkTypeEnum.TwoG, NetworkTypeEnum.SlowTwoG].includes(networkType)
    ) {
      config[GoogleCaptchaVersionEnum.V3].FIRST_LEVEL_TIMEOUT = 5000;
      config[GoogleCaptchaVersionEnum.V3].EXECUTE_TIMEOUT = 60000;
      config[GoogleCaptchaVersionEnum.V2].FIRST_LEVEL_TIMEOUT = 4000;
      config[GoogleCaptchaVersionEnum.V2].RENDER_TIMEOUT = 20000;
      config[GoogleCaptchaVersionEnum.V2].SUCCESS_TIMEOUT = 12000;
      config[GoogleCaptchaVersionEnum.V2].EXECUTE_TIMEOUT = 60 * 1000;
    }

    config[GoogleCaptchaVersionEnum.V2].RENDER_TIMEOUT = addBy(
      config[GoogleCaptchaVersionEnum.V2].RENDER_TIMEOUT,
      waitingExcute,
    );
    return config;
  }, [networkType]);

  const lang = useMemo(() => {
    const langMap: any = {
      'en-US': 'en',
      'zh-CN': 'zh-CN',
    };
    return langMap[localLanguage] || 'en';
  }, [localLanguage]);

  const loadScriptConfig = useMemo(() => {
    // 谷歌验证一级资源链接
    const url =
      version === GoogleCaptchaVersionEnum.V3
        ? `${googleApi}?render=${API.reCaptureV3SiteKey}`
        : `${googleApi}?render=explicit&hl=${lang}`;

    // v3/v2 一级资源脚本加载超时时间
    const timeout = CAPTCHA_CONFIG[version].FIRST_LEVEL_TIMEOUT;

    return { url, timeout };
  }, [lang, version]);

  // 提前定义清理函数，供回调使用
  const clearV2Timer = () => {
    if (v2RenderTimerRef.current) {
      clearTimeout(v2RenderTimerRef.current);
      v2RenderTimerRef.current = null;
    }
  };

  // 清理 V2 验证勾选人机验证后执行超时定时器
  const clearV2ExecuteTimer = () => {
    if (v2ExecuteTimerRef.current) {
      clearTimeout(v2ExecuteTimerRef.current);
      v2ExecuteTimerRef.current = null;
    }
  };

  // 移除整个谷歌验证框的最外层容器（z-index: 2000000000）
  const clearGoogleCaptchaContainer = () => {
    const reCaptchaContainer = document.querySelector(
      'div[style*="z-index: 2000000000"]',
    );
    if (reCaptchaContainer) {
      reCaptchaContainer.remove(); // 移除整个验证框容器
    }
  };

  /**
   * 彻底清理资源
   * 彻底清理 reCAPTCHA 残留，为下次重新初始化扫清障碍(避免资源加载失败可能导致的 recaptcha 损坏出现伪token,进而导致 验证已过期)
   */
  const clearRecaptchaResources = () => {
    // 组件卸载时，关闭状态更新开关
    isMountedRef.current = false;
    setLoadFlag(false);
    // 组件卸载时清除 V2 定时器
    clearV2Timer();
    // 清理 V2 执行超时定时器
    clearV2ExecuteTimer();

    // 移除整个谷歌验证框的最外层容器
    clearGoogleCaptchaContainer();

    // 组件卸载时移除已加载的 reCAPTCHA 一级资源 script 标签
    // 防止组件卸载后，遗留的 script 标签影响其他组件或下次初始化
    const existingScripts = document.querySelectorAll(
      'script[src*="recaptcha/api.js"]',
    );
    existingScripts.forEach((script) => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    });

    // 同时清理全局 grecaptcha 对象
    try {
      // @ts-ignore
      delete window?.grecaptcha;
    } catch {
      // @ts-ignore
      window.grecaptcha = undefined;
    }
  };

  // 统一的失败降级处理函数（防止重复调用）
  const handleFail = (errorType: GoogleCaptureErrorEnum) => {
    if (isFailedRef.current || !isMountedRef.current) return;
    isFailedRef.current = true;
    setReqStatus(StatusTipEnum.error);
    onFail?.(errorType);
    // 彻底清理资源
    clearRecaptchaResources();
  };

  // 全局雷达次级资源监控
  const {
    resourceError: recaptchaResLoadFailed,
    networkError: recaptchaNetworkError,
  } = useRecaptchaResourceMonitor({
    onError: (url) => {
      // 雷达探测到一级资源异常
      if (url === loadScriptConfig.url) {
        handleFail(GoogleCaptureErrorEnum.FIRST_LEVEL_LOAD_FAILED);
        return;
      }
      // 雷达探测到次级资源异常 触发降级
      handleFail(GoogleCaptureErrorEnum.SECOND_LEVEL_LOAD_FAILED);
    },
  });

  // 从 Error 对象中提取枚举类型
  const extractErrorType = (error: any): GoogleCaptureErrorEnum => {
    const msg = error?.message;
    // 检查 message 是否在定义的枚举值列表中
    const isValidEnum = Object.values(GoogleCaptureErrorEnum).includes(msg);
    return isValidEnum ? msg : GoogleCaptureErrorEnum.UNKNOWN_ERROR;
  };

  // 加载js
  const loadScript = ({ url, timeout }: { url: string; timeout: number }) => {
    return new Promise((resolve, reject) => {
      // 防止重复加载
      const existingScript = document.querySelector(`script[src="${url}"]`);
      if (existingScript) {
        resolve({ code: 0 });
        return;
      }

      // const timeStr = `网络：${
      //   (navigator as any).connection?.effectiveType || '-'
      // }, ${
      //   version === GoogleCaptchaVersionEnum.V3 ? 'v3' : 'v2'
      // }一级资源加载耗时`;
      // console.time(timeStr);
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.defer = true;

      let timer: NodeJS.Timeout | null = null;
      let isSettled = false; // 标记 Promise 是否已完成

      const cleanUp = () => {
        if (timer) clearTimeout(timer);
        script.onload = null;
        script.onerror = null;
      };

      // 成功加载
      script.onload = () => {
        if (isSettled) return;
        isSettled = true;
        // console.timeEnd(timeStr);
        cleanUp();
        resolve({ code: 0 });
      };

      // 网络中断或请求失败 (如 404, DNS 解析失败)
      script.onerror = () => {
        if (isSettled) return;
        // console.timeEnd(timeStr);
        isSettled = true;
        cleanUp();
        // 主动移除加载失败的破损 script 标签，防止影响后续重试 & 杜绝了“超时后又突然弹出来”的情况
        if (script.parentNode) script.parentNode.removeChild(script);
        // 谷歌验证一级资源加载失败
        reject(new Error(GoogleCaptureErrorEnum.FIRST_LEVEL_LOAD_FAILED));
      };

      // 超时中断
      timer = setTimeout(() => {
        if (isSettled) return;
        isSettled = true;
        cleanUp();
        // 超时后主动移除 script 标签，中断后续可能迟到的加载 & 杜绝了“超时后又突然弹出来”的情况
        if (script.parentNode) script.parentNode.removeChild(script);
        // 谷歌验证一级资源加载超时
        reject(new Error(GoogleCaptureErrorEnum.FIRST_LEVEL_TIMEOUT));
      }, timeout);

      document.head.appendChild(script);
    });
  };

  // 渲染验证码
  const onCaptchaRender = async () => {
    try {
      if (!isMountedRef.current) return;

      // 前置短路：如果雷达已经报错，直接拦截
      if (recaptchaResLoadFailed) {
        // 次级资源已确认失败
        throw new Error(GoogleCaptureErrorEnum.SECOND_LEVEL_LOAD_FAILED);
      }

      // 前置短路：网络错误
      if (recaptchaNetworkError) {
        throw new Error(GoogleCaptureErrorEnum.NETWORK_ERROR);
      }

      // @ts-ignore
      if (!window?.grecaptcha) {
        // grecaptcha 对象未成功初始化
        throw new Error(GoogleCaptureErrorEnum.SCRIPT_ERROR);
      }

      // const timeStr = `网络：${
      //   (navigator as any).connection?.effectiveType || '-'
      // }, ${
      //   version === GoogleCaptchaVersionEnum.V3 ? 'v3' : 'v2'
      // }验证执行&次级资源加载耗时`;
      // 谷歌验证码 V3 验证执行
      if (version === GoogleCaptchaVersionEnum.V3) {
        // 验证执行超时定时器
        let executeTimeoutTimer: NodeJS.Timeout | null = null;

        // await console.time(timeStr);
        // 验证
        const executePromise = new Promise<string>((resolve, reject) => {
          // @ts-ignore
          window.grecaptcha.ready(() => {
            // @ts-ignore
            window.grecaptcha
              .execute(API.reCaptureV3SiteKey, { action: 'submit' })
              .then(resolve)
              .catch((err: any) => {
                console.log('V3 验证执行失败: ', err);
                // 验证执行失败
                reject(new Error(GoogleCaptureErrorEnum.EXECUTE_ERROR));
              });
          });
        });

        // V3 验证执行超时
        const timeoutPromise = new Promise<string>(
          (_, reject) =>
            (executeTimeoutTimer = setTimeout(() => {
              // 验证执行超时
              reject(new Error(GoogleCaptureErrorEnum.EXECUTE_TIMEOUT));
            }, CAPTCHA_CONFIG[version].EXECUTE_TIMEOUT)),
        );

        // 赛跑：谁先完成听谁的
        const token = await Promise.race([executePromise, timeoutPromise]);

        // 无论谁赢，只要 race 结束，立刻清掉定时器
        if (executeTimeoutTimer) clearTimeout(executeTimeoutTimer);

        if (!isMountedRef.current) return;

        // await console.timeEnd(timeStr);
        setReqStatus(StatusTipEnum.success);
        if (token) {
          onChange?.(token);
        } else {
          handleFail(GoogleCaptureErrorEnum.EXECUTE_ERROR);
        }
      } else {
        // 谷歌验证码 V2 验证执行
        // await console.time(timeStr);
        // @ts-ignore
        window.grecaptcha.ready(() => {
          if (!isMountedRef.current) return;
          // @ts-ignore
          const widgetId = window.grecaptcha?.render(captchaRef.current!, {
            sitekey: API.reCaptureV2SiteKey,
            callback: (token: any) => {
              if (!isMountedRef.current) return;
              setReqStatus(StatusTipEnum.success);
              // 验证成功，清除兜底定时器
              clearV2Timer();
              clearV2ExecuteTimer();
              onChange?.(token);
            },
            // V2 内部在 reCAPTCHA 遇到错误（通常是网络连接问题、Google 内部在拉取资源失败时）
            'error-callback': () => {
              // 内部报错，清除兜底定时器
              clearV2Timer();
              clearV2ExecuteTimer();
              // 未成功时控制页面显示 “加载失败 刷新页面”
              setReqStatus(StatusTipEnum.error);
            },
            // // 在 reCAPTCHA 响应到期且用户需要重新验证时执行的回调函数
            // 'expired-callback': () => {
            //   console.log('expired-callback');
            //   onChange?.('');
            //   // @ts-ignore
            //   window.grecaptcha?.reset(captchaWidgetRef.current);
            // },
          });
          // 保存 widgetId 以备后续重置
          // captchaWidgetRef.current = widgetId;

          // console.timeEnd(timeStr);
          // render 执行完毕，iframe 已插入 DOM
          // 给 Google iframe 内部留出渲染初始 Loading 动画的时间
          setTimeout(() => {
            if (!isMountedRef.current) return;
            // 此时 iframe 内部已有内容，撤下外层 Loading，实现无缝衔接
            setReqStatus(StatusTipEnum.success);
          }, CAPTCHA_CONFIG[version].SUCCESS_TIMEOUT);

          // V2 启动隐性超时兜底定时器，并将其存入 Ref
          v2RenderTimerRef.current = setTimeout(() => {
            if (!isMountedRef.current) return;

            handleFail(GoogleCaptureErrorEnum.EXECUTE_TIMEOUT);
          }, CAPTCHA_CONFIG[version].RENDER_TIMEOUT);
        });
      }
    } catch (error: any) {
      console.log(`reCAPTCHA ${version} 异常:`, error);
      // 提取错误类型并触发降级
      const errorType = extractErrorType(error);

      // 如果是因为组件卸载导致的，不向父组件报错（静默处理）
      if (errorType !== GoogleCaptureErrorEnum.COMPONENT_UNMOUNTED) {
        handleFail(errorType);
      }
    }
  };

  // 初始化
  const onInit = async () => {
    if (!isMountedRef.current) return;
    setReqStatus(StatusTipEnum.loading);
    try {
      // 优先加载一级资源,待加载完成后再渲染验证码,避免因为一级资源加载失败而导致验证码渲染失败,出现window.grecaptcha 未就绪的情况
      // @ts-ignore
      await loadScript(loadScriptConfig);
      if (!isMountedRef.current) return;
      onCaptchaRender();
    } catch (e: any) {
      console.error('load fail', e);
      const errorType = extractErrorType(e);
      if (errorType !== GoogleCaptureErrorEnum.COMPONENT_UNMOUNTED) {
        handleFail(errorType);
      } else {
        clearGoogleCaptchaContainer();
        setReqStatus(StatusTipEnum.error);
      }
    }
  };

  useEffect(() => {
    if (loadFlag) {
      onInit();
    }
  }, [loadFlag]);

  useEffect(() => {
    // 雷达报错: 资源加载错误
    if (recaptchaResLoadFailed) {
      handleFail(GoogleCaptureErrorEnum.SECOND_LEVEL_LOAD_FAILED);
    }
    // 雷达报错: 网络错误
    if (recaptchaNetworkError) {
      handleFail(GoogleCaptureErrorEnum.NETWORK_ERROR);
    }
  }, [recaptchaNetworkError, recaptchaResLoadFailed, handleFail]);

  /**
   * 谷歌v2验证
   * 极端网络情况下 勾选人机验证后 受谷歌内部Promise超时调用reject抛出未经过error-callback通知宿主导致的无响应情况增加时限处理
   * 当用户未勾选人机验证时由 CAPTCHA_CONFIG[v2].RENDER_TIMEOUT 兜底
   * 当用户勾选人机验证后由 CAPTCHA_CONFIG[v2].EXECUTE_TIMEOUT 兜底
   */
  useEffect(() => {
    if (version !== GoogleCaptchaVersionEnum.V2) return;

    // 监听窗口失去焦点（跨域 iframe 被点击时，父页面会失去焦点）
    const handleBlur = () => {
      // 只有当组件存在，且尚未失败时，才启动定时器
      if (captchaRef.current && isMountedRef.current) {
        // 启动防卡死定时器
        v2ExecuteTimerRef.current = setTimeout(() => {
          if (!isMountedRef.current) return;

          // 点击后长时间无响应（未收到 token 也无报错），判定为 reCAPTCHA 卡死，触发降级
          handleFail(GoogleCaptureErrorEnum.EXECUTE_TIMEOUT);
        }, CAPTCHA_CONFIG[version].EXECUTE_TIMEOUT);
      }
    };

    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('blur', handleBlur);
      if (v2ExecuteTimerRef.current) clearTimeout(v2ExecuteTimerRef.current);
    };
  }, [
    version,
    CAPTCHA_CONFIG,
    handleFail,
    clearV2Timer,
    clearGoogleCaptchaContainer,
  ]);

  useEffect(() => {
    isMountedRef.current = true;
    isFailedRef.current = false;
    return () => {
      // 彻底清理资源
      clearRecaptchaResources();
    };
  }, []);

  return version === GoogleCaptchaVersionEnum.V2 ? (
    <div className="google-capture-components">
      <div
        ref={captchaRef}
        className={classNames('hide', {
          'dp-block': reqStatus === StatusTipEnum.success,
        })}
      />
      <DefaultPage
        status={
          reqStatus === StatusTipEnum.error
            ? DefaultPageStatusEnum.NO_NETWORK
            : reqStatus
        }
        style={
          reqStatus !== StatusTipEnum.success ? { width: 304, height: 85 } : {}
        }
        containerStyle={{ lineHeight: 'normal' }}
        retryText={intlGet('COMMON.REFRESH.PAGE')}
        onRetry={() => window?.location.reload()}
      />
    </div>
  ) : (
    ''
  );
}

export default connect(({ CommonModel }: ConnectState) => ({
  localLanguage: CommonModel.localLanguage,
}))(GooglCapture);
